import { byPosition, keyBetween, type BoardSnapshot, type CardSummary, type UpdateCardInput } from '@kaba/shared';
import { api, ApiError, newOpId } from './api';
import { boardKey, cardKey, isNetworkError, newUuid, queryClient, setOfflineHooks, upsertCards } from './board';
import { loadOutbox, outbox, setupOutbox, type NewOutboxOp, type OutboxOp, type SendResult } from './outbox';
import { realtime } from './realtime';

/**
 * Verbindet Outbox, Netz und Board-Cache (0.44). Regeln:
 * - Erst versuchen, dann ablegen: Ohne Verbindung (Fehler 0) oder mit ausstehenden Änderungen derselben
 *   Karte kommt eine Änderung in die Outbox; der lokale Stand bleibt sichtbar.
 * - Übertragen beim Wiederverbinden, beim Start und alle 30 s, solange etwas aussteht.
 */


/** Muss diese Änderung in die Outbox (statt sofort gesendet zu werden)? */
export function mustQueue(cardId: string): boolean {
  try {
    return (typeof navigator !== 'undefined' && navigator.onLine === false) || outbox().hasPendingFor(cardId);
  } catch {
    return false;
  }
}

const send = async (op: OutboxOp, expectedVersion: number | undefined): Promise<SendResult> => {
  try {
    let card: CardSummary | undefined;
    if (op.kind === 'planning') {
      if (!/^\/api\/me\/planning\/(blocks\/[0-9a-f-]+|prefs|sources\/[0-9a-f-]+|connections\/[0-9a-f-]+)$/.test(op.url)) return {ok:false,reason:'failed',message:'Ungültiges Planungsziel.'};
      if (op.method === 'PUT') await api.put(op.url, op.body); else await api.del(op.url, op.body);
      void queryClient.invalidateQueries({queryKey:['planning']});
      return {ok:true};
    } else if (op.kind === 'create') {
      card = await api.post<CardSummary>(`/api/boards/${op.boardId}/cards`, { id: op.cardId, columnId: op.columnId, title: op.title, placement: op.placement, opId: op.id });
    } else if (op.kind === 'move') {
      const r = await api.post<{ cards: CardSummary[] }>(`/api/cards/${op.cardId}/move`, { columnId: op.columnId, beforeId: op.beforeId, afterId: op.afterId, opId: op.id });
      upsertCards(queryClient, op.boardId, r.cards);
      card = r.cards.find((c) => c.id === op.cardId);
    } else if (op.kind === 'update') {
      card = await api.patch<CardSummary>(`/api/cards/${op.cardId}`, { ...op.patch, expectedVersion, opId: op.id });
    } else if (op.kind === 'labels' || op.kind === 'assignees') {
      card = await api.patch<CardSummary>(`/api/cards/${op.cardId}/${op.kind}`, { add: op.add, remove: op.remove });
    } else if (op.kind === 'checkAdd') {
      await api.post(`/api/cards/${op.cardId}/checklist`, { id: op.itemId, text: op.text });
    } else if (op.kind === 'checkSet') {
      await api.patch(`/api/checklist/${op.itemId}`, { done: op.done });
    } else if (op.kind === 'checkDel') {
      // Schon gelöscht (auch von jemand anderem) ist ebenfalls erledigt
      await api.del(`/api/checklist/${op.itemId}`).catch((e) => {
        if (!(e instanceof ApiError && e.status === 404)) throw e;
      });
    } else if (op.kind === 'field') {
      card = await api.put<CardSummary>(`/api/cards/${op.cardId}/fields`, { fieldId: op.fieldId, value: op.value, checkExpected: true, expected: op.expected });
    } else if (op.kind === 'archive') {
      await api.del(`/api/cards/${op.cardId}`);
      queryClient.setQueryData<BoardSnapshot>(boardKey(op.boardId), (s) => (s ? { ...s, cards: s.cards.filter((c) => c.id !== op.cardId) } : s));
    } else if (op.kind === 'restore') {
      card = await api.post<CardSummary>(`/api/cards/${op.cardId}/restore`);
    } else {
      await api.post(`/api/cards/${op.cardId}/comments`, { body: op.body, format: 2, clientId: op.clientId });
      void queryClient.invalidateQueries({ queryKey: ['comments', op.cardId] });
    }
    if (op.kind.startsWith('check')) void queryClient.invalidateQueries({ queryKey: cardKey(op.cardId) });
    if (card && 'id' in card) upsertCards(queryClient, op.boardId, [card]);
    return { ok: true, card: card && 'id' in card ? card : undefined };
  } catch (e) {
    const err = e as ApiError;
    // Keine Verbindung, Server gerade weg oder Sitzung abgelaufen: später erneut
    if (!(err instanceof ApiError) || err.status === 0 || err.status === 401 || err.status >= 500) return { ok: false, reason: 'offline' };
    if (op.kind === 'planning') return {ok:false,reason:err.status===409?'conflict':'failed',message:err.message};
    const data = err.data as { card?: CardSummary } | undefined;
    if (err.status === 409) {
      if (data?.card) upsertCards(queryClient, op.boardId, [data.card]);
      const now = data?.card && op.kind === 'update' && op.patch.title !== undefined && data.card.title !== op.patch.title ? ` Titel jetzt: „${short(data.card.title)}“.` : '';
      if (op.kind === 'field') return { ok: false, reason: 'conflict', message: err.message, card: data?.card };
      return { ok: false, reason: 'conflict', message: `Jemand anderes hat die Karte inzwischen geändert.${now}`, card: data?.card };
    }
    if (op.kind === 'checkSet' && err.status === 404) return { ok: false, reason: 'failed', message: 'Der Checklistenpunkt wurde inzwischen gelöscht.' };
    return { ok: false, reason: 'failed', message: err.status === 403 || err.status === 404 ? 'Die Karte oder das Board ist nicht mehr zugänglich.' : err.message };
  }
};

let timer: ReturnType<typeof setInterval> | undefined;
export function initOfflineSync() {
  setOfflineHooks({ mustQueue, queueUpdate, queueMove, createOffline, isOfflineCreated, queueCardOp });
  const ob = setupOutbox(send, (boardIds) => {
    for (const b of boardIds.filter(Boolean)) void queryClient.invalidateQueries({ queryKey: boardKey(b) });
  });
  const kick = () => {
    if (ob.pendingCount() && navigator.onLine !== false) void ob.flush();
  };
  window.addEventListener('online', kick);
  realtime.onStatus(() => {
    if (realtime.status === 'online') kick();
  });
  timer = setInterval(kick, 30_000);
  return ob;
}
export function stopOfflineSync() {
  clearInterval(timer);
}

/** Nach der Anmeldung: Ausstehendes dieses Kontos laden und übertragen */
export async function startOutbox(account: string) {
  if (outbox().account === account) return;
  await loadOutbox(account);
  if (navigator.onLine !== false) void outbox().flush();
}

/** Karte wurde offline angelegt und ist noch nicht auf dem Server */
export function isOfflineCreated(cardId: string): boolean {
  try {
    return outbox()
      .getSnapshot()
      .some((o) => o.kind === 'create' && o.cardId === cardId);
  } catch {
    return false;
  }
}

/* ---------- Ablegen aus den Mutationen ---------- */

const short = (t: string) => (t.length > 40 ? `${t.slice(0, 39)}…` : t);
function cardTitle(boardId: string, cardId: string) {
  return queryClient.getQueryData<BoardSnapshot>(boardKey(boardId))?.cards.find((c) => c.id === cardId)?.title ?? 'Karte';
}

export function describePatch(p: UpdateCardInput): string {
  const parts: string[] = [];
  if (p.title !== undefined) parts.push(`Titel „${short(p.title)}“`);
  if (p.description !== undefined) parts.push('Beschreibung');
  if (p.dueAt !== undefined || p.dueHasTime !== undefined) parts.push(p.dueAt === null ? 'Frist entfernt' : 'Frist');
  if (p.startAt !== undefined || p.startHasTime !== undefined) parts.push(p.startAt === null ? 'Beginn entfernt' : 'Beginn');
  if (p.priority !== undefined) parts.push('Priorität');
  if (p.blocked !== undefined || p.blockedReason !== undefined) parts.push(p.blocked === false ? 'Blockade aufgehoben' : 'Blockiert');
  if (p.completed !== undefined) parts.push(p.completed ? 'erledigt' : 'wieder offen');
  if (p.ownerId !== undefined) parts.push('Hauptverantwortlich');
  return parts.join(', ') || 'Änderung';
}

export async function queueUpdate(boardId: string, cardId: string, patch: UpdateCardInput, before?: CardSummary) {
  const { expectedVersion, opId: _o, ...rest } = patch;
  void _o;
  const card = queryClient.getQueryData<BoardSnapshot>(boardKey(boardId))?.cards.find((c) => c.id === cardId);
  await outbox().add({
    id: newOpId(),
    kind: 'update',
    boardId,
    cardId,
    label: `„${short(before?.title ?? cardTitle(boardId, cardId))}“: ${describePatch(patch)}`,
    patch: rest,
    baseVersion: expectedVersion ?? card?.version ?? 0,
    chained: outbox().hasPendingFor(cardId),
  });
}

export async function queueMove(boardId: string, cardId: string, columnId: string, beforeId: string | null, afterId: string | null) {
  const col = queryClient.getQueryData<BoardSnapshot>(boardKey(boardId))?.columns.find((c) => c.id === columnId);
  await outbox().add({ id: newOpId(), kind: 'move', boardId, cardId, columnId, beforeId, afterId, label: `„${short(cardTitle(boardId, cardId))}“ nach „${col?.title ?? 'Spalte'}“ verschoben` });
}

/** Weitere Kartenänderungen ablegen (0.45); `what` beschreibt sie für die Liste */
export async function queueCardOp(op: NewOutboxOp, what: string) {
  await outbox().add({ ...op, label: `„${short(cardTitle(op.boardId, op.cardId))}“: ${what}` } as NewOutboxOp);
}

/** Neue Karte ohne Verbindung: sofort im Board, Anlage später (gleiche ID) */
export async function createOffline(boardId: string, input: { columnId: string; title: string; placement?: 'top' | 'bottom' }, id = newUuid(), opId = newOpId()): Promise<CardSummary> {
  const snap = queryClient.getQueryData<BoardSnapshot>(boardKey(boardId));
  const inCol = (snap?.cards ?? []).filter((c) => c.columnId === input.columnId).sort(byPosition);
  const placement = input.placement ?? 'bottom';
  let position = '';
  try {
    position = placement === 'top' ? keyBetween(null, inCol[0]?.position ?? null) : keyBetween(inCol.at(-1)?.position ?? null, null);
  } catch {
    position = inCol.at(-1)?.position ?? 'a0';
  }
  const now = new Date().toISOString();
  const card: CardSummary = {
    id,
    boardId,
    columnId: input.columnId,
    position,
    title: input.title,
    hasDescription: false,
    descriptionRev: 0,
    startAt: null,
    startHasTime: false,
    dueAt: null,
    dueHasTime: false,
    priority: 0,
    blocked: false,
    blockedReason: null,
    completedAt: null,
    cover: null,
    labelIds: [],
    assigneeIds: [],
    ownerId: null,
    checklistTotal: 0,
    checklistDone: 0,
    attachmentCount: 0,
    commentCount: 0,
    recurrence: null,
    estimateMinutes: null,
    spentMinutes: 0,
    timerUserIds: [],
    custom: {},
    version: 1,
    updatedAt: now,
    archivedAt: null,
    dependsOn: [],
  };
  upsertCards(queryClient, boardId, [card]);
  await outbox().add({ id: opId, kind: 'create', boardId, cardId: id, columnId: input.columnId, title: input.title, placement, label: `Neue Karte „${short(input.title)}“` });
  return card;
}

/** Verwerfen: Ablage entfernen und den Serverstand neu laden */
export async function discardOp(op: OutboxOp) {
  await outbox().remove(op.id);
  if (op.kind === 'create') queryClient.setQueryData<BoardSnapshot>(boardKey(op.boardId), (s) => (s ? { ...s, cards: s.cards.filter((c) => c.id !== op.cardId) } : s));
  void queryClient.invalidateQueries({ queryKey: boardKey(op.boardId) });
  void queryClient.invalidateQueries({ queryKey: cardKey(op.cardId) });
}

/** Konflikt: eigene Änderung bewusst übernehmen – mit der aktuellen Version vom Server */
export async function forceOp(op: OutboxOp) {
  let version: number | undefined;
  if (op.kind === 'update' || op.kind === 'field') {
    try {
      const cur = await api.get<CardSummary>(`/api/cards/${op.cardId}`);
      version = cur.version;
      // Feld: den jetzt gültigen Wert als „gesehen“ eintragen – die eigene Änderung gilt dann bewusst
      if (op.kind === 'field') await outbox().amend(op.id, { expected: cur.custom[op.fieldId] ?? null } as Partial<OutboxOp>);
    } catch {
      /* ohne Verbindung: später */
    }
  }
  await outbox().retry(op.id, op.kind === 'update' ? version : undefined);
}

export { isNetworkError, newUuid };
